/**
 * QMP-FE-1 (F3) — the plan as Quality Setup reads it.
 *
 * ONE READ, `GET /projects/{id}/qmp`, and the keys the wizard steps
 * consume (NL21-A2 §6.2, A3, A4): `structure`, `packages`, `standard_rules`,
 * `standard_options`, `standard_steps`, `can_publish`, `publish_blockers`, `approvals_summary`, `current_revision`,
 * `approval_due`, `signing_statement`, `approval_act` (QMP-FE-8a).
 *
 * NO LOCAL OPTIMISM. A step that saves calls `reload()` afterwards and shows
 * what the server now says. A response that arrives after a newer request was
 * started is dropped, so a slow first read cannot overwrite a fresh one.
 * `reload()` resolves `true` when this read landed, `false` when it failed and
 * `null` when a newer read superseded it (QMP-FE-8a: "recorded, but the plan
 * could not be read again" is its own state on the Approvals page).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getProjectQmp } from '../lib/api';
import { apiErrorMessage } from '../pages/TestRegisterPage';

export default function useProjectQmp(projectId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const seq = useRef(0);

  const reload = useCallback(async () => {
    seq.current += 1;
    const mine = seq.current;
    setLoading(true);
    try {
      const res = await getProjectQmp(projectId);
      if (mine !== seq.current) return null;
      setData(res?.data || null);
      setError('');
      return true;
    } catch (err) {
      if (mine !== seq.current) return null;
      setError(apiErrorMessage(err, 'Could not load the Quality Setup'));
      return false;
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    reload();
    return () => { seq.current += 1; };
  }, [reload]);

  return {
    loading,
    error,
    reload,
    structure: data?.structure || [],
    packages: data?.packages || {},
    standard_rules: data?.standard_rules || null,
    // QMP-FE-9 — the choices of every field and the sub-steps (QMP-BE-3): the
    // producers of the dedicated read, served with the plan. Null until served.
    standard_options: data?.standard_options && typeof data.standard_options === 'object'
      ? data.standard_options : null,
    standard_steps: Array.isArray(data?.standard_steps) ? data.standard_steps : null,
    // SIGN-1 (D26) — the "Before you sign" statement and its version, as
    // served. Null until served: no statement, no box, nothing to tick.
    signing_statement: data?.signing_statement && typeof data.signing_statement === 'object'
      ? data.signing_statement : null,
    can_publish: Boolean(data?.can_publish),
    publish_blockers: data?.publish_blockers || [],
    approvals_summary: data?.approvals_summary || null,
    // QMP-FE-5 — step 6: the revision this one follows, and the approval due
    // the server fixes (not the page's to choose).
    current_revision: data?.current_revision || null,
    approval_due: data?.approval_due || null,
    // QMP-FE-6 — step 7: the concrete day the answers are due (a LABEL: the
    // server never approves by time) and the revisions declared so far.
    approvals_due_at: data?.approvals_due_at || null,
    history: data?.history || [],
    // QMP-FE-8a (D30) — the one act the reader's company may sign, as served
    // (`{revision, participation_id, pending[], can_decide}`); null otherwise —
    // and null is "not served", never "nothing pending" (the step says so).
    approval_act: data?.approval_act && typeof data.approval_act === 'object'
      ? data.approval_act : null,
  };
}
